// src/components/DashboardExpenses.js
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  updateDoc,
  doc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../firebase';

// Sottoscrizione per le spese COMPLETE (isComplete === true)
export const subscribeToExpenses = (userId, userFamily, setExpenses) => {
  let expensesQuery;
  if (userFamily?.id) {
    expensesQuery = query(
      collection(db, 'expenses'),
      where('familyId', '==', userFamily.id),
      where('isComplete', '==', true),
      orderBy('date', 'desc')
    );
  } else {
    expensesQuery = query(
      collection(db, 'expenses'),
      where('userId', '==', userId),
      where('familyId', '==', null),
      where('isComplete', '==', true),
      orderBy('date', 'desc')
    );
  }
  return onSnapshot(expensesQuery, (snapshot) => {
    const expenses = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setExpenses(expenses);
  });
};

// Sottoscrizione per le spese INCOMPLETE (isComplete === false)
// Con filtro temporale fisso: vengono considerati solo i documenti con 'date' > cutoff.
// In più, per ogni documento, se i campi 'name' e 'category' (trimmed) contengono almeno un carattere,
// viene eseguito un update per impostare isComplete a true.
export const subscribeToIncompleteExpenses = (userId, userFamily, setIncompleteExpenses) => {
  // Imposta il cutoff fisso: escludiamo le spese con data minore o uguale al 12/02/2025.
  const cutoff = new Date("2025-02-12T00:00:00");
  let expensesQuery;
  if (userFamily?.id) {
    expensesQuery = query(
      collection(db, 'expenses'),
      where('familyId', '==', userFamily.id),
      where('isComplete', '==', false),
      where('date', '>', cutoff),
      orderBy('date', 'desc')
    );
  } else {
    expensesQuery = query(
      collection(db, 'expenses'),
      where('userId', '==', userId),
      where('familyId', '==', null),
      where('isComplete', '==', false),
      where('date', '>', cutoff),
      orderBy('date', 'desc')
    );
  }
  return onSnapshot(expensesQuery, (snapshot) => {
    snapshot.docs.forEach((docSnap) => {
      const data = docSnap.data();
      const trimmedName = (data.name || "").trim();
      const trimmedCategory = (data.category || "").trim();
      // Se entrambi i campi hanno almeno un carattere e isComplete è ancora false, aggiorna il documento
      if (trimmedName.length > 0 && trimmedCategory.length > 0) {
        docSnap.ref.update({ isComplete: true }).catch(error => {
          console.error(`Errore nell'aggiornamento della spesa ${docSnap.id}:`, error);
        });
      }
    });
    // Aggiorna lo stato (i documenti che restano incomplete)
    const expenses = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setIncompleteExpenses(expenses);
  });
};

// Sottoscrizione per le spese RICORRENTI
export const subscribeToRecurringExpenses = (userId, userFamily, setRecurringExpenses) => {
  let recurringExpensesQuery;
  if (userFamily?.id) {
    recurringExpensesQuery = query(
      collection(db, 'recurringExpenses'),
      where('familyId', '==', userFamily.id),
      orderBy('createdAt', 'desc')
    );
  } else {
    recurringExpensesQuery = query(
      collection(db, 'recurringExpenses'),
      where('userId', '==', userId),
      where('familyId', '==', null),
      orderBy('createdAt', 'desc')
    );
  }
  return onSnapshot(recurringExpensesQuery, (snapshot) => {
    const recurringExpenses = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setRecurringExpenses(recurringExpenses);
  });
};

// Funzione che sottoscrive tutte le spese (complete, incomplete e ricorrenti)
// Ritorna una funzione di cleanup che annulla tutte le sottoscrizioni.
export const subscribeToAllExpenses = (
  userId,
  userFamily,
  setExpenses,
  setRecurringExpenses,
  setIncompleteExpenses
) => {
  const unsubscribeComplete = subscribeToExpenses(userId, userFamily, setExpenses);
  const unsubscribeIncomplete = subscribeToIncompleteExpenses(userId, userFamily, setIncompleteExpenses);
  const unsubscribeRecurring = subscribeToRecurringExpenses(userId, userFamily, setRecurringExpenses);
  return () => {
    unsubscribeComplete();
    unsubscribeIncomplete();
    unsubscribeRecurring();
  };
};

// Aggiorna una spesa (quando l'utente completa i dettagli) e la marca come completa.
export const updateExpense = async (expenseId, updatedName, updatedCategory) => {
  try {
    const expenseRef = doc(db, 'expenses', expenseId);
    await updateDoc(expenseRef, {
      name: updatedName,
      category: updatedCategory,
      isComplete: true,
    });
    alert('Spesa aggiornata con successo!');
  } catch (err) {
    console.error("Errore nell'aggiornamento della spesa:", err);
    alert("Errore nell'aggiornamento della spesa.");
  }
};

// Aggiorna una spesa ricorrente.
export const updateRecurringExpense = async (expenseId, updates) => {
  try {
    const expenseRef = doc(db, 'recurringExpenses', expenseId);
    await updateDoc(expenseRef, updates);
    return true;
  } catch (error) {
    console.error('Errore nell\'aggiornamento della spesa ricorrente:', error);
    throw error;
  }
};

// Elimina una spesa ricorrente.
export const deleteRecurringExpense = async (expenseId) => {
  try {
    await deleteDoc(doc(db, 'recurringExpenses', expenseId));
    return true;
  } catch (error) {
    console.error('Errore nella cancellazione della spesa ricorrente:', error);
    throw error;
  }
};
